if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

let skuIndexPromise = null;
let synonymIndexPromise = null;

// Longest phrase (in words) we try to match against the synonym index,
// so "sinus lift" wins over "sinus" when both are present.
const SYNONYM_MAX_PHRASE = 3;

// Don't let a hanging CDN request block a form submit forever.
const INDEX_TIMEOUT = 1500;

function normalizeText(value) {
  return String(value)
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/aa/g, 'a')
    .replace(/ae/g, 'a')
    .replace(/oe/g, 'o');
}

// Lookup key for both index keys and customer queries: folded to plain ASCII
// and stripped of everything that isn't a letter or a digit, so "PRF-100.2",
// "prf 100 2" and "prf1002" all land on the same entry.
function normalizeKey(value) {
  return normalizeText(value).replace(/[^a-z0-9]/g, '');
}

// Re-key a fetched index through the current normalizer, so the JSON files can
// stay human-readable ("blodrør", "PRF-100.2") without the lookup caring.
function normalizeIndex(map) {
  const index = {};

  for (const key in map) {
    const normalized = normalizeKey(key);
    if (normalized) index[normalized] = map[key];
  }

  return index;
}

function fetchJson(url) {
  return fetch(url)
    .then((response) => (response.ok ? response.json() : {}))
    .catch(() => ({}));
}

function loadSkuIndex(urls) {
  if (!skuIndexPromise) {
    skuIndexPromise = Promise.all(urls.map(fetchJson)).then((parts) => normalizeIndex(Object.assign({}, ...parts)));
  }
  return skuIndexPromise;
}

function loadSynonymIndex(url) {
  if (!synonymIndexPromise) {
    synonymIndexPromise = fetchJson(url).then(normalizeIndex);
  }
  return synonymIndexPromise;
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;
        this.skuIndex = null;
        this.synonymIndex = null;
        this.indexPromise = null;
        this.indexesReady = false;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));

        // The SKU and synonym indexes are only fetched once the customer shows
        // intent to search. Pages where nobody touches the search field pay
        // nothing for them.
        ['focusin', 'pointerenter', 'touchstart'].forEach((event) => {
          this.addEventListener(event, this.loadIndexes.bind(this), { once: true, passive: true });
        });
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      loadIndexes() {
        if (this.indexPromise) return this.indexPromise;

        const pending = [];

        const skuIndexUrls = this.getAttribute('data-sku-index-urls');
        if (skuIndexUrls) {
          const urls = skuIndexUrls.split(',').filter(Boolean);
          pending.push(
            loadSkuIndex(urls).then((index) => {
              this.skuIndex = index;
            })
          );
        }

        const synonymUrl = this.getAttribute('data-synonym-url');
        if (synonymUrl) {
          pending.push(
            loadSynonymIndex(synonymUrl).then((index) => {
              this.synonymIndex = index;
            })
          );
        }

        this.indexPromise = Promise.race([
          Promise.all(pending),
          new Promise((resolve) => setTimeout(resolve, INDEX_TIMEOUT)),
        ]).then(() => {
          this.indexesReady = true;
        });

        return this.indexPromise;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          this.loadIndexes().then(() => {
            this.renderSection(this.buildUrl().toString(), event);
          });
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      // Word-by-word synonym replacement, so "gutta perka rør" resolves just as
      // well as "guttaperka". Returns null when nothing matched.
      resolveSynonyms(rawQuery) {
        const index = this.synonymIndex;
        if (!index) return null;

        const tokens = rawQuery.split(/\s+/).filter(Boolean);
        if (tokens.length === 0) return null;

        const parts = [];
        let replaced = false;

        for (let i = 0; i < tokens.length; i++) {
          let matched = false;

          for (let size = Math.min(SYNONYM_MAX_PHRASE, tokens.length - i); size >= 1; size--) {
            const key = normalizeKey(tokens.slice(i, i + size).join(''));

            if (key && index[key]) {
              parts.push(index[key]);
              i += size - 1;
              matched = true;
              replaced = true;
              break;
            }
          }

          if (!matched) parts.push(tokens[i]);
        }

        if (!replaced) return null;

        // "prf prf rør" adds nothing over "prf rør".
        return parts
          .filter((part, i) => i === 0 || normalizeKey(part) !== normalizeKey(parts[i - 1]))
          .join(' ');
      }

      resolveQuery(rawQuery) {
        if (!rawQuery) return rawQuery;

        // 1. Synonyms and typos — e.g. "blodplader" → "PRF", "gutta perka" → "Gutta percha"
        const resolved = this.resolveSynonyms(rawQuery);
        if (resolved !== null) return resolved;

        if (!this.skuIndex) return rawQuery;

        const normalized = normalizeKey(rawQuery);
        if (!normalized) return rawQuery;

        // 2. Exact SKU match
        if (this.skuIndex[normalized]) return this.skuIndex[normalized];

        // 3. Prefix SKU match — find all entries whose key starts with the normalized query,
        //    then return the longest common prefix of their original SKU values so
        //    Shopify can show progressive results while typing without dashes/dots.
        const matchingValues = [];
        for (const key in this.skuIndex) {
          if (key.startsWith(normalized)) matchingValues.push(this.skuIndex[key]);
        }

        if (matchingValues.length === 0) return rawQuery;

        let prefix = matchingValues[0];
        for (let i = 1; i < matchingValues.length; i++) {
          let j = 0;
          while (j < prefix.length && j < matchingValues[i].length && prefix[j] === matchingValues[i][j]) j++;
          prefix = prefix.slice(0, j);
        }

        return prefix || rawQuery;
      }

      applyResolvedQuery() {
        const query = this.getQuery();
        const resolved = this.resolveQuery(query);

        if (resolved !== query) this.input.value = resolved;
      }

      onSubmit(event) {
        if (this.indexesReady) {
          this.applyResolvedQuery();
          return;
        }

        // Indexes still in flight: hold the submit just long enough to rewrite
        // the query, then submit natively (which skips this handler).
        event.preventDefault();

        this.loadIndexes().then(() => {
          this.applyResolvedQuery();
          this.submit();
        });
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.resolveQuery(this.getQuery()));
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        this.loadIndexes().then(() => {
          this.renderSection(this.buildUrl().toString());
        });
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
